import { v4 as uuidv4 } from 'uuid';

import { MessagingAvailabilityReason, MessagingStatus } from 'providers/messaging/MessagingProvider';
import { sendCounterMetric } from 'utils/metrics';

import lpTag from './liveEngageTag.generated';
import { getCurrentConfig, UNLAUNCHED_LOCALES } from './config';
import { getSDEs, sendChatData } from './utils';
import { LpChatMessage } from './types';
import { chatMessageListener } from './chatEvents';

const STATE_TIMEOUT = 20000;
const { siteId, locales } = getCurrentConfig();
let currentEngagementId: number;

const isAllowUnlaunchedFlagPresent = () => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  return urlParams.get('lp_messaging_allow_unlaunched') === 'true';
};

const getEngagementId = (locale: string): number => {
  const engagementId = locales[locale];
  if (!engagementId) {
    return 0;
  }
  const isLaunched = !UNLAUNCHED_LOCALES.includes(locale);
  return isLaunched || isAllowUnlaunchedFlagPresent() ? engagementId : 0;
};

const getLpDivId = () => `LP_DIV_${currentEngagementId}`;

const createLpContainer = () => {
  const id = getLpDivId();
  if (!window.document.getElementById(`${id}`)) {
    const el = window.document.createElement('div');
    el.setAttribute('id', id);
    window.document.body.append(el);
  }
};

const updateEngagement = (locale: string) => {
  createLpContainer();
  window.lpTag?.newPage?.(window.location.href, {
    section: [locale],
    sdes: [],
    taglets: {
      rendererStub: {
        divIdsToKeep: {
          [getLpDivId()]: true,
        },
      },
    },
  });
};

const onMessagingReady = (callback: Function) => {
  window.lpTag.events.bind({
    eventName: 'AFTER_CREATE_ENGAGEMENT_INSTANCE',
    appName: 'RENDERER_STUB',
    func: callback,
    triggerOnce: true,
  });
  window.lpTag.hooks = window.lpTag.hooks || [];
  window.lpTag.hooks.push({
    name: 'AFTER_GET_LINES',
    callback: (options: { data: { lines?: LpChatMessage[] } }) => {
      const { lines } = options.data;
      if (lines) {
        chatMessageListener(lines);
      }
      return options;
    },
  });
};

const setupMessaging = () =>
  new Promise<void>(resolve => {
    onMessagingReady(() => {
      resolve();
    });
  });

const init = async (): Promise<MessagingStatus> => {
  const setupMessagingPromise = setupMessaging();
  return new Promise(async resolve => {
    const stateTimeout = setTimeout(() => {
      resolve({
        isAvailable: false,
        reason: MessagingAvailabilityReason.TIMEOUT,
      });
    }, STATE_TIMEOUT);
    await setupMessagingPromise;
    clearTimeout(stateTimeout);
    resolve({
      isAvailable: true,
      reason: MessagingAvailabilityReason.AVAILABLE,
    });
  });
};

export const startLiveEngage = async (market: string, locale: string) => {
  const supportRequestId = uuidv4();
  const spoSDEs = await getSDEs(supportRequestId);

  sendChatData(market, supportRequestId);
  window.lpTag.sdes = window.lpTag.sdes || [];
  window.lpTag.sdes.push(spoSDEs);
  window.lpTag.taglets.rendererStub.click(currentEngagementId);

  sendCounterMetric('live-engage-conversation-started', { locale });
};

export const setup = async (locale: string): Promise<MessagingStatus> => {
  currentEngagementId = getEngagementId(locale);
  if (currentEngagementId) {
    updateEngagement(locale);
    return init();
  }
  return { isAvailable: false, reason: MessagingAvailabilityReason.UNSUPPORTED_LOCALE };
};

export const setupLpTag = () => {
  lpTag(siteId);
};
